(() => {
  if (window.atkFaceitInitialized) return;
  window.atkFaceitInitialized = true;

  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const tickerCleanups = new WeakMap();
  const tickerRefreshes = new WeakMap();

  function initializeTicker(root) {
    const track = root.querySelector('[data-faceit-ticker-track]');
    const group = root.querySelector('[data-faceit-ticker-group]');
    const unit = group.querySelector('.atk-faceit-page__ticker-unit');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame;

    function refresh() {
      if (!root.isConnected) return;
      const previousDuration = parseFloat(track.style.getPropertyValue('--faceit-ticker-duration'));
      const currentTime = track.getAnimations()[0]?.currentTime;
      const progress = previousDuration && typeof currentTime === 'number' ? (currentTime / 1000 / previousDuration) % 1 : 0;
      track.classList.remove('is-ready');
      group.replaceChildren(unit);
      track.replaceChildren(group);
      if (reduced.matches || !root.clientWidth) return;

      const unitWidth = unit.getBoundingClientRect().width;
      if (!unitWidth) return;
      while (group.getBoundingClientRect().width < root.clientWidth + unitWidth) {
        const repeated = unit.cloneNode(true);
        repeated.setAttribute('aria-hidden', 'true');
        group.append(repeated);
      }
      const duplicate = group.cloneNode(true);
      duplicate.removeAttribute('data-faceit-ticker-group');
      duplicate.setAttribute('aria-hidden', 'true');
      track.append(duplicate);
      const distance = duplicate.getBoundingClientRect().left - group.getBoundingClientRect().left;
      const duration = distance / (window.matchMedia('(max-width: 600px)').matches ? 20 : 28);
      track.style.setProperty('--faceit-ticker-distance', `${distance}px`);
      track.style.setProperty('--faceit-ticker-duration', `${duration}s`);
      track.style.animationDelay = `${-progress * duration}s`;
      void track.offsetWidth;
      track.classList.add('is-ready');
    }

    function schedule() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(refresh);
    }

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    observer?.observe(root);
    reduced.addEventListener('change', schedule);
    document.fonts?.ready.then(schedule);
    document.fonts?.addEventListener?.('loadingdone', schedule);
    tickerRefreshes.set(root, schedule);
    tickerCleanups.set(root, () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      reduced.removeEventListener('change', schedule);
      document.fonts?.removeEventListener?.('loadingdone', schedule);
    });
    schedule();
  }

  function updateProductCarousel(carousel) {
    const track = carousel.querySelector('[data-atk-event-carousel-track]');
    if (!track) return;
    const image = track.querySelector('.product-item__image-wrapper');
    if (image) carousel.style.setProperty('--atk-ep-carousel-image-height', `${image.getBoundingClientRect().height}px`);
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    carousel.classList.toggle('is-scrollable', max > 2);
    const previous = carousel.querySelector('[data-atk-event-carousel-prev]');
    const next = carousel.querySelector('[data-atk-event-carousel-next]');
    if (previous) previous.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= max - 1;
    const progress = carousel.querySelector('[data-atk-event-carousel-progress]');
    if (progress) {
      const width = max > 0 ? Math.max(16, track.clientWidth / track.scrollWidth * 100) : 100;
      progress.style.setProperty('--atk-ep-progress-width', `${width}%`);
      progress.style.setProperty('--atk-ep-progress-left', `${max > 0 ? track.scrollLeft / max * (100 - width) : 0}%`);
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-faceit-coupon]:not([data-faceit-ready])').forEach((button) => {
      button.dataset.faceitReady = 'true';
      const label = button.querySelector('[data-faceit-coupon-label]');
      const originalLabel = label.textContent;
      let resetTimer;
      let copying = false;
      button.addEventListener('click', async () => {
        if (copying) return;
        copying = true;
        clearTimeout(resetTimer);
        try {
          await navigator.clipboard.writeText(button.dataset.faceitCoupon);
          label.textContent = 'Code copied!';
          button.classList.add('is-copied');
          resetTimer = setTimeout(() => {
            label.textContent = originalLabel;
            button.classList.remove('is-copied');
          }, 2000);
        } catch {
          window.prompt('Copy your discount code:', button.dataset.faceitCoupon);
          label.textContent = originalLabel;
          button.classList.remove('is-copied');
        } finally {
          copying = false;
        }
      });
    });

    scope.querySelectorAll('[data-faceit-direct-link]:not([data-faceit-link-ready])').forEach((link) => {
      link.dataset.faceitLinkReady = 'true';
      const url = new URL(link.href);
      const directHref = `${url.origin}${url.pathname}`;
      const restoreHref = () => {
        if (link.href !== directHref) link.href = directHref;
      };
      new MutationObserver(restoreHref).observe(link, { attributes: true, attributeFilter: ['href'] });
      restoreHref();
      link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        window.open(directHref, '_blank', 'noopener,noreferrer');
      });
    });

    scope.querySelectorAll('[data-faceit-ticker]:not([data-faceit-ready])').forEach((root) => {
      root.dataset.faceitReady = 'true';
      initializeTicker(root);
    });

    scope.querySelectorAll('.atk-faceit-top-picks [data-atk-event-tabs]:not([data-faceit-ready])').forEach((group) => {
      group.dataset.faceitReady = 'true';
      const tabs = [...group.querySelectorAll('[data-atk-event-tab]')];
      const panels = [...group.querySelectorAll('[data-atk-event-panel]')];
      function activate(tab) {
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.dataset.atkEventPanel !== tab.dataset.atkEventTab; });
        group.querySelectorAll('[data-atk-event-carousel]').forEach(updateProductCarousel);
      }
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!direction && event.key !== 'Home' && event.key !== 'End') return;
          event.preventDefault();
          const target = tabs[event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + direction + tabs.length) % tabs.length];
          target.focus();
          activate(target);
        });
      });
    });

    scope.querySelectorAll('.atk-faceit-top-picks [data-atk-event-carousel]:not([data-faceit-ready])').forEach((carousel) => {
      carousel.dataset.faceitReady = 'true';
      const track = carousel.querySelector('[data-atk-event-carousel-track]');
      carousel.querySelector('[data-atk-event-carousel-prev]')?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: motion() }));
      carousel.querySelector('[data-atk-event-carousel-next]')?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: motion() }));
      track.addEventListener('scroll', () => updateProductCarousel(carousel), { passive: true });
      updateProductCarousel(carousel);
    });
  }

  initialize();
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-faceit-ticker]').forEach((root) => {
      if (!('ResizeObserver' in window)) tickerRefreshes.get(root)?.();
    });
    document.querySelectorAll('.atk-faceit-top-picks [data-atk-event-carousel]').forEach(updateProductCarousel);
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-faceit-ticker]').forEach((root) => tickerCleanups.get(root)?.());
  });
})();
